import React, { useState, useMemo } from 'react';
import { Upload, message, Form, Image } from 'antd';
import { PlusOutlined } from '@ant-design/icons';
import type { UploadFile, UploadProps, RcFile } from 'antd/es/upload/interface';
import type { YhUploadProps } from './interface';
import { formatFile, formatFileList } from '@src/utils/uploadUtils';

// 10MB 大小限制 (字节)
const MAX_FILE_SIZE = 10 * 1024 * 1024;

const YhUploadForm: React.FC<YhUploadProps> = ({
  name = 'fileList',
  label = '上传文件',
  maxCount = 4,
  initialValue = [],
  allowedTypes = [".jpg", ".png", ".jpeg", ".svg", ".gif", ".pdf", ".doc", ".xls", ".xlsx"],
  onChange,
  fileUrl = (url) => url,
  action,
  uploadData,
  listType = 'picture-card',
  uploadButton,
  maxSize = MAX_FILE_SIZE,
  ...restProps
}) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [previewImage, setPreviewImage] = useState('');
  const [fileList, setFileList] = useState(initialValue);

  // 文件类型检查
  const isValidFileType = (fileName: string): boolean => {
    const fileExt = fileName.substring(fileName.lastIndexOf(".")).toLowerCase();
    return allowedTypes.includes(fileExt);
  };

  // 上传前检查
  const beforeUpload = (file: RcFile): boolean | Promise<void> => {
    // 检查文件类型
    if (!isValidFileType(file.name)) {
      message.error(`文件类型不支持，请上传${allowedTypes.join(',')}格式的文件`);
      return Promise.reject(new Error('文件类型不支持'));
    }

    // 检查文件大小
    if (file.size > maxSize) {
      message.error(`文件过大，请上传小于${maxSize / 1024 / 1024}MB的文件`);
      return Promise.reject(new Error('文件过大'));
    }

    return true;
  };

  // 处理预览
  const handlePreview = (file: UploadFile) => {
    const isDoc = file?.name?.toLowerCase().endsWith('.pdf') ||
      file?.name?.toLowerCase().endsWith('.doc') ||
      file?.name?.toLowerCase().endsWith('.docx') ||
      file?.name?.toLowerCase().endsWith('.xls') ||
      file?.name?.toLowerCase().endsWith('.xlsx');

    if (isDoc) {
      // 下载
      const downloadLink = document.createElement('a');
      downloadLink.href = file?.url || '';
      downloadLink.target = '_blank';
      downloadLink.rel = 'noopener noreferrer';
      const fileName = file?.name || `文件.${file?.type?.split('/')[1] || 'pdf'}`;
      downloadLink.download = fileName;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    }
    else {
      setPreviewImage(file?.url || '');
      setPreviewOpen(true);
    }
  };

  // 处理文件变化
  const handleChange: UploadProps['onChange'] = (info) => {
    if (info.file.status === 'done') {
      if (info.file.response && info.file.response.retCode === 0) {
        const index = info.fileList.findIndex(f => f.uid === info.file.uid);
        if (index !== -1) {
          info.fileList[index] = formatFile(info.file);
        }
      }

      const formattedList = formatFileList(info.fileList);
      setFileList(formattedList);
      onChange?.(formattedList);
    } else if (info.file.status === 'error') {
      message.error('文件上传失败，请重新上传!');
    } else {
      // 处理删除等其他状态变化
      const formattedList = formatFileList(info.fileList);
      setFileList(formattedList);
      onChange?.(formattedList);
    }
  };

  return (
    <>
      <Form.Item
        name={name}
        label={label}
        valuePropName="fileList"
        initialValue={initialValue}
        getValueFromEvent={info => Array.isArray(info) ? info : info?.fileList}
        {...restProps}
      >
        <Upload
          name="file"
          action={action}
          data={uploadData}
          listType={listType}
          maxCount={maxCount}
          beforeUpload={beforeUpload}
          onPreview={handlePreview}
          onChange={handleChange}
          fileList={fileList}
        >
          {fileList.length >= maxCount ? null : (uploadButton || <PlusOutlined />)}
        </Upload>
      </Form.Item>

      <Image
        width={200}
        style={{ display: 'none' }}
        src={previewImage}
        preview={{
          visible: previewOpen,
          src: previewImage,
          onVisibleChange: setPreviewOpen,
        }}
      />
    </>
  );
};

export default YhUploadForm; 